/**
 * Error Logging Service
 *
 * Centralized error logging with console output in development
 * and Sentry integration for production error tracking.
 *
 * @module lib/logging/errorLogger
 */

import { AppError, ErrorSeverity } from '../errors/AppError';
import { toAppError } from '../errors/errorCategories';
import { captureException, setTag } from './sentry';

/**
 * Log context for additional information
 */
export interface LogContext {
  userId?: string;
  component?: string;
  feature?: string;
  action?: string;
  extra?: Record<string, unknown>;
}

/**
 * Configuration for error logger
 */
interface LoggerConfig {
  isDevelopment: boolean;
  enableConsole: boolean;
  enableSentry: boolean;
  sentryDsn?: string;
}

/**
 * Logger configuration (default to development mode)
 */
let config: LoggerConfig = {
  isDevelopment: import.meta.env.DEV,
  enableConsole: true,
  enableSentry: false,
};

/**
 * Configure the error logger
 */
export function configureLogger(newConfig: Partial<LoggerConfig>): void {
  config = { ...config, ...newConfig };
}

/**
 * Map ErrorSeverity to console methods
 */
function getConsoleMethod(severity: ErrorSeverity): keyof Console {
  switch (severity) {
    case ErrorSeverity.CRITICAL:
      return 'error';
    case ErrorSeverity.ERROR:
      return 'error';
    case ErrorSeverity.WARNING:
      return 'warn';
    case ErrorSeverity.INFO:
      return 'info';
    default:
      return 'log';
  }
}

/**
 * Log to console (development)
 */
function logToConsole(error: AppError, context?: LogContext): void {
  if (!config.enableConsole) {
    return;
  }

  const consoleMethod = getConsoleMethod(error.severity);
  const prefix = `[${error.severity.toUpperCase()}] [${error.category}]`;

  const logFn = console[consoleMethod] as (...args: unknown[]) => void;

  // Log basic error info
  logFn(`${prefix} ${error.message}`, {
    timestamp: error.timestamp,
    code: error.code,
    details: error.details,
    context,
    stack: error.stack,
  });

  // In development, also log the full error object for debugging
  if (config.isDevelopment) {
    console.groupCollapsed(`${prefix} Full Error Details`);
    console.log('Error Object:', error);
    console.log('Context:', context);
    console.groupEnd();
  }
}

/**
 * Log to Sentry (production)
 */
function logToSentry(error: AppError, context?: LogContext): void {
  // Only send to Sentry in production
  if (!import.meta.env.PROD) {
    return;
  }

  // Respect the enableSentry config flag
  if (!config.enableSentry) {
    return;
  }

  try {
    // Set error category and severity as tags
    setTag('error.category', error.category);
    setTag('error.severity', error.severity);

    if (error.code) {
      setTag('error.code', error.code.toString());
    }

    // Set component and feature tags
    if (context?.component) {
      setTag('component', context.component);
    }
    if (context?.feature) {
      setTag('feature', context.feature);
    }
    if (context?.action) {
      setTag('action', context.action);
    }

    // Build context for Sentry
    const sentryContext: Record<string, string | number | boolean> = {
      timestamp: error.timestamp,
      message: error.message,
      category: error.category,
      severity: error.severity,
    };

    if (error.code) {
      sentryContext.code = error.code;
    }

    if (error.details) {
      sentryContext.details = String(error.details);
    }

    if (context?.userId) {
      sentryContext.userId = context.userId;
    }

    // Capture the error with context
    captureException(error, sentryContext);
  } catch (sentryError) {
    // Fallback if Sentry fails - don't break the app
    console.error('[Sentry] Failed to log error:', sentryError);
  }
}

/**
 * Main error logging function
 *
 * @param error - The error to log (can be any type)
 * @param context - Additional context information
 */
export function logError(error: unknown, context?: LogContext): void {
  // Convert to AppError if needed
  const appError = toAppError(error);

  // Sanitize context (remove sensitive data)
  const sanitizedContext = sanitizeContext(context);

  // Log to console (development)
  logToConsole(appError, sanitizedContext);

  // Log to Sentry (production)
  logToSentry(appError, sanitizedContext);
}

/**
 * Sanitize context to remove sensitive information
 */
function sanitizeContext(context?: LogContext): LogContext | undefined {
  if (!context) {
    return undefined;
  }

  const sanitized = { ...context };

  // Remove sensitive fields from extra
  if (sanitized.extra) {
    const sensitiveKeys = ['password', 'token', 'apiKey', 'secret'];
    const safeExtra: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(sanitized.extra)) {
      if (!sensitiveKeys.includes(key)) {
        safeExtra[key] = value;
      }
    }

    sanitized.extra = safeExtra;
  }

  return sanitized;
}

/**
 * Log error with automatic component detection (for use in components)
 *
 * @param error - The error to log
 * @param componentName - Name of the component
 * @param additionalContext - Additional context
 */
export function logComponentError(
  error: unknown,
  componentName: string,
  additionalContext?: Partial<LogContext>
): void {
  logError(error, {
    ...additionalContext,
    component: componentName,
  });
}

/**
 * Log API error with request details
 *
 * @param error - The error to log
 * @param endpoint - API endpoint
 * @param method - HTTP method
 * @param additionalContext - Additional context
 */
export function logApiError(
  error: unknown,
  endpoint: string,
  method: string,
  additionalContext?: Partial<LogContext>
): void {
  logError(error, {
    ...additionalContext,
    action: `${method} ${endpoint}`,
    extra: {
      ...additionalContext?.extra,
      endpoint,
      method,
    },
  });
}
